import { eventChannel } from 'redux-saga';
import { call, put, take, takeLatest } from 'redux-saga/effects';
import type { ChecklistTask } from '@nx/domain';
import { checklistRepository } from '../../services/eoRepositories';
import { pushToast } from '../slices/eoUiSlice';
import {
  checklistFailed,
  checklistLoaded,
  checklistSaveSucceeded,
  checklistTaskCreateRequested,
  checklistTaskUpdateRequested,
  checklistTemplateApplyRequested,
  checklistWatchRequested,
} from '../slices/checklistSlice';

function* watchChecklist(eventId: string): Generator<unknown, void, unknown> {
  const channel = eventChannel<ChecklistTask[]>((emit) =>
    checklistRepository.watchByEvent(eventId, (items) => emit(items)),
  );
  try {
    while (true) {
      yield put(checklistLoaded((yield take(channel)) as ChecklistTask[]));
    }
  } finally {
    channel.close();
  }
}

function* handleWatch(action: ReturnType<typeof checklistWatchRequested>): Generator<unknown, void, unknown> {
  yield call(watchChecklist, action.payload);
}

function* handleCreate(action: ReturnType<typeof checklistTaskCreateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [checklistRepository, checklistRepository.create],
      action.payload.eventId,
      action.payload.input,
    );
    yield put(checklistSaveSucceeded());
    yield put(pushToast({ msg: 'eo.toast.checklistTaskAdded', kind: 'success' }));
  } catch (e) {
    yield put(checklistFailed(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleUpdate(action: ReturnType<typeof checklistTaskUpdateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [checklistRepository, checklistRepository.update],
      action.payload.eventId,
      action.payload.taskId,
      action.payload.input,
    );
    yield put(checklistSaveSucceeded());
  } catch (e) {
    yield put(checklistFailed(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleTemplate(action: ReturnType<typeof checklistTemplateApplyRequested>): Generator<unknown, void, unknown> {
  try {
    const count = (yield call(
      [checklistRepository, checklistRepository.applyTemplate],
      action.payload.eventId,
      action.payload.templateId,
    )) as number;
    yield put(
      pushToast({
        msg: 'eo.toast.checklistTemplateApplied',
        params: { count: String(count) },
        kind: 'success',
      }),
    );
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.checklistTemplateFailed', kind: 'danger' }));
  }
}

export function* checklistSaga() {
  yield takeLatest(checklistWatchRequested.type, handleWatch);
  yield takeLatest(checklistTaskCreateRequested.type, handleCreate);
  yield takeLatest(checklistTaskUpdateRequested.type, handleUpdate);
  yield takeLatest(checklistTemplateApplyRequested.type, handleTemplate);
}
